import { useState, useEffect, useCallback, Fragment } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../../../api/client';
import { useUIStore } from '../../../stores/uiStore';
import { usePagination } from '../../../hooks/usePagination';
import { Pagination } from '../../../components/shared/Pagination';
import { Loader2, ChevronDown, ChevronRight, AlertTriangle, Search } from 'lucide-react';
import { useCan } from '../../../lib/rbac';

// Vista de familias (2026-10-22, solo lectura). Contrato: "familia desconocida" y "un miembro
// observado" NO son sueltos confirmados; todavía no hay plan registrado; fuente = nuestra base.

type Filtro = 'familia' | 'un_miembro' | 'desconocida' | 'todas';

interface Fila {
  clave: string;
  estado: 'familia' | 'un_miembro' | 'desconocida';
  nombre: string | null;
  titulo_ejemplo: string | null;
  miembros: number;
  categorias: number;
  categoria: string | null;
  asins: string[];
  ventas_90d: number;
  actualizado: string | null;
}

interface Detalle {
  estado: Fila['estado'];
  family_id: string | null;
  miembros: Array<{
    item_id: string;
    asin: string | null;
    titulo: string | null;
    estado: string;
    categoria: string | null;
    diferencias: Record<string, string>;
    ventas_90d: number;
    actualizado: string | null;
    paises: Array<{
      site_id: string;
      site_item_id: string | null;
      estado: string | null;
      price: number | null;
      currency_id: string | null;
      permalink: string | null;
      titulo: string | null;
    }>;
  }>;
  amazon: { padre: string | null; variantes: Array<{ asin: string; dimensiones: Record<string, string>; en_este_vendedor: string }> } | null;
  plan_sombra: {
    version: string;
    calculado: string;
    por_asin: Record<string, { tipo: 'familia' | 'suelto' | 'frenado'; grupo?: number; ejes?: Record<string, string>; motivo: string }>;
  } | null;
}

const firma = (d: Record<string, string>) =>
  Object.entries(d)
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([k, v]) => `${k}=${String(v).toLowerCase().trim()}`)
    .join(';');

const chip = 'px-1.5 py-0.5 rounded text-[10px] font-mono border';
const ok = 'bg-emerald-100 text-emerald-700 border-emerald-300 dark:bg-emerald-900/40 dark:text-emerald-300 dark:border-emerald-600';
const bad = 'bg-red-100 text-red-700 border-red-300 dark:bg-red-900/40 dark:text-red-300 dark:border-red-600';
const neutral = 'bg-[var(--color-bg-tertiary)] text-[var(--color-text-secondary)] border-[var(--color-border)]';

function DetalleFamilia({ clave, sellerId }: { clave: string; sellerId: number }) {
  const { t } = useTranslation();
  const canMoney = useCan()('global.view_money');
  const [d, setD] = useState<Detalle | null>(null);
  const [err, setErr] = useState(false);

  useEffect(() => {
    let vivo = true;
    api
      .get<any>(`/publisher/published/families/${encodeURIComponent(clave)}?seller_id=${sellerId}`)
      .then((res) => {
        if (!vivo) return;
        if (res.success && res.data) setD(res.data);
        else setErr(true);
      })
      .catch(() => vivo && setErr(true));
    return () => {
      vivo = false;
    };
  }, [clave, sellerId]);

  if (err) return <div className="p-4 text-xs text-red-400">{t('pub.families.loadError')}</div>;
  if (!d) return <div className="p-4"><Loader2 className="w-4 h-4 animate-spin text-[var(--color-primary)]" /></div>;

  // Miembros con exactamente las mismas diferencias dentro de una familia = el comprador no los distingue.
  const cuenta = new Map<string, number>();
  for (const m of d.miembros) cuenta.set(firma(m.diferencias), (cuenta.get(firma(m.diferencias)) ?? 0) + 1);
  const esFamilia = d.miembros.length > 1;

  return (
    <div className="p-3 space-y-3 bg-[var(--color-bg-primary)]">
      <div className="text-[11px] text-[var(--color-text-tertiary)]">
        {d.plan_sombra
          ? t('pub.families.shadowPlan', { fecha: new Date(d.plan_sombra.calculado).toLocaleString() })
          : t('pub.families.noPlan')}{' '}
        · {t('pub.families.source')}
        {d.family_id && <> · {t('pub.families.familyId')} <span className="font-mono">{d.family_id}</span></>}
      </div>

      <table className="w-full text-xs">
        <thead>
          <tr className="text-[var(--color-text-secondary)]">
            <th className="text-left py-1.5 px-2 font-medium">ASIN</th>
            <th className="text-left py-1.5 px-2 font-medium">{t('pub.families.title')}</th>
            <th className="text-left py-1.5 px-2 font-medium">{t('pub.families.differences')}</th>
            <th className="text-left py-1.5 px-2 font-medium">{t('pub.families.countries')}</th>
            <th className="text-right py-1.5 px-2 font-medium">{t('pub.families.sales90')}</th>
            {d.plan_sombra && <th className="text-left py-1.5 px-2 font-medium">{t('pub.families.newPlan')}</th>}
          </tr>
        </thead>
        <tbody>
          {d.miembros.map((m) => {
            const f = firma(m.diferencias);
            const sinDif = esFamilia && Object.keys(m.diferencias).length === 0;
            const repetido = esFamilia && !sinDif && (cuenta.get(f) ?? 0) > 1;
            return (
              <tr key={m.item_id} className="border-t border-[var(--color-border)] align-top">
                <td className="py-1.5 px-2">
                  <div className="font-mono text-blue-400">{m.asin || '—'}</div>
                  <div className="font-mono text-[10px] text-[var(--color-text-tertiary)]">{m.item_id}</div>
                </td>
                <td className="py-1.5 px-2 max-w-[260px] text-[var(--color-text)]">{m.titulo || '—'}</td>
                <td className="py-1.5 px-2">
                  <div className="flex flex-wrap gap-1">
                    {Object.entries(m.diferencias).map(([k, v]) => (
                      <span key={k} className={`${chip} ${repetido ? bad : neutral}`}>{k}: {v}</span>
                    ))}
                    {sinDif && (
                      <span className={`${chip} ${bad} flex items-center gap-1`}>
                        <AlertTriangle size={10} /> {t('pub.families.noDifferences')}
                      </span>
                    )}
                    {repetido && <span className={`${chip} ${bad}`}>{t('pub.families.identical')}</span>}
                  </div>
                </td>
                <td className="py-1.5 px-2">
                  <div className="flex flex-wrap gap-1">
                    {m.paises.map((p) => {
                      const vivo = p.estado === 'active' || p.estado === 'paused';
                      const texto = `${p.site_id}${canMoney && p.price != null ? ` ${p.price} ${p.currency_id ?? ''}` : ''}`;
                      return p.permalink ? (
                        <a key={p.site_id} href={p.permalink} target="_blank" rel="noreferrer" title={p.titulo ?? ''}
                          className={`${chip} ${vivo ? ok : bad} hover:underline`}>{texto}</a>
                      ) : (
                        <span key={p.site_id} title={p.titulo ?? ''} className={`${chip} ${vivo ? ok : bad}`}>{texto}</span>
                      );
                    })}
                    {m.paises.length === 0 && <span className="text-[var(--color-text-tertiary)]">—</span>}
                  </div>
                </td>
                <td className="py-1.5 px-2 text-right font-mono">{m.ventas_90d}</td>
                {d.plan_sombra && (() => {
                  const p = m.asin ? d.plan_sombra.por_asin[m.asin] : undefined;
                  if (!p) return <td className="py-1.5 px-2 text-[var(--color-text-tertiary)]">—</td>;
                  const color = p.tipo === 'familia' ? ok : p.tipo === 'frenado' ? bad : neutral;
                  const etiqueta = p.tipo === 'familia'
                    ? `${t('pub.families.planFamily', { n: p.grupo })}${p.ejes ? ' · ' + Object.values(p.ejes).join(' / ') : ''}`
                    : p.tipo === 'frenado' ? t('pub.families.planHeld') : t('pub.families.planStandalone');
                  return (
                    <td className="py-1.5 px-2">
                      <span className={`${chip} ${color}`} title={p.motivo}>{etiqueta}</span>
                      <div className="text-[10px] text-[var(--color-text-tertiary)] max-w-[220px]">{p.motivo}</div>
                    </td>
                  );
                })()}
              </tr>
            );
          })}
        </tbody>
      </table>

      {d.amazon && (
        <div>
          <div className="text-[11px] font-medium text-[var(--color-text-secondary)] mb-1">
            {t('pub.families.amazonVariants', { n: d.amazon.variantes.length, parent: d.amazon.padre })}
          </div>
          <div className="flex flex-wrap gap-1">
            {d.amazon.variantes.map((v) => (
              <span key={v.asin} className={`${chip} ${v.en_este_vendedor === 'publicada' ? ok : neutral}`}
                title={v.en_este_vendedor}>
                {v.asin} · {Object.values(v.dimensiones).join(' / ') || '—'} · {v.en_este_vendedor}
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function FamiliesView() {
  const { t } = useTranslation();
  const { sellerId } = useUIStore();
  const pag = usePagination('pub-families');
  const [filtro, setFiltro] = useState<Filtro>('familia');
  const [q, setQ] = useState('');
  const [qAplicada, setQAplicada] = useState('');
  const [items, setItems] = useState<Fila[]>([]);
  const [resumen, setResumen] = useState<{ familias: number; avisos_en_familias: number; un_miembro: number; desconocida: number } | null>(null);
  const [loading, setLoading] = useState(true);
  const [abierta, setAbierta] = useState<string | null>(null);

  const cargar = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ seller_id: String(sellerId), page: String(pag.page), limit: String(pag.perPage), filter: filtro });
      if (qAplicada) params.set('q', qAplicada);
      const res = await api.get<any>(`/publisher/published/families?${params}`);
      if (res.success && res.data) {
        setItems(res.data.items || []);
        setResumen(res.data.resumen || null);
        pag.setTotal(res.data.total || 0);
      }
    } catch (e) {
      console.error('Failed to load families:', e);
    } finally {
      setLoading(false);
    }
  }, [sellerId, pag.page, pag.perPage, filtro, qAplicada]);

  useEffect(() => { cargar(); }, [cargar]);

  const filtros: Array<{ key: Filtro; label: string; n?: number }> = [
    { key: 'familia', label: t('pub.families.filterFamilies'), n: resumen?.familias },
    { key: 'un_miembro', label: t('pub.families.filterSingle'), n: resumen?.un_miembro },
    { key: 'desconocida', label: t('pub.families.filterUnknown'), n: resumen?.desconocida },
    { key: 'todas', label: t('pub.families.filterAll') },
  ];

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        {filtros.map((f) => (
          <button key={f.key}
            onClick={() => { setFiltro(f.key); setAbierta(null); pag.resetPage(); }}
            className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition-colors ${
              filtro === f.key
                ? 'bg-[var(--color-primary)] text-white border-[var(--color-primary)]'
                : 'bg-[var(--color-bg-tertiary)] text-[var(--color-text-secondary)] border-[var(--color-border)] hover:text-[var(--color-text)]'
            }`}>
            {f.label}{f.n != null && ` (${Number(f.n).toLocaleString()})`}
          </button>
        ))}
        <form className="flex items-center gap-1 ml-auto" onSubmit={(e) => { e.preventDefault(); setQAplicada(q); setAbierta(null); pag.resetPage(); }}>
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder={t('pub.families.searchPlaceholder')}
            className="px-2 py-1.5 rounded-lg text-xs bg-[var(--color-bg-tertiary)] border border-[var(--color-border)] text-[var(--color-text)] w-56" />
          <button type="submit" className="p-1.5 rounded-lg border border-[var(--color-border)] text-[var(--color-text-secondary)]"><Search size={14} /></button>
        </form>
      </div>
      {resumen && filtro === 'familia' && (
        <div className="text-xs text-[var(--color-text-secondary)]">
          {t('pub.families.summary', { familias: resumen.familias.toLocaleString(), avisos: resumen.avisos_en_familias.toLocaleString() })}
        </div>
      )}

      <div className="bg-[var(--color-bg-secondary)] rounded-lg border border-[var(--color-border)] overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-[var(--color-border)]">
              <th className="w-6" />
              <th className="text-left px-3 py-2.5 text-[var(--color-text-secondary)] font-medium text-xs">{t('pub.families.name')}</th>
              <th className="text-center px-3 py-2.5 text-[var(--color-text-secondary)] font-medium text-xs">{t('pub.families.members')}</th>
              <th className="text-left px-3 py-2.5 text-[var(--color-text-secondary)] font-medium text-xs">{t('pub.families.category')}</th>
              <th className="text-right px-3 py-2.5 text-[var(--color-text-secondary)] font-medium text-xs">{t('pub.families.sales90')}</th>
              <th className="text-left px-3 py-2.5 text-[var(--color-text-secondary)] font-medium text-xs">{t('pub.families.updated')}</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={6} className="text-center py-12"><Loader2 className="w-5 h-5 animate-spin mx-auto text-[var(--color-primary)]" /></td></tr>
            ) : items.length === 0 ? (
              <tr><td colSpan={6} className="text-center py-12 text-[var(--color-text-tertiary)]">{t('pub.families.empty')}</td></tr>
            ) : items.map((r) => (
              <Fragment key={r.clave}>
                <tr onClick={() => setAbierta(abierta === r.clave ? null : r.clave)}
                  className="border-b border-[var(--color-border)] hover:bg-[var(--color-bg-tertiary)] cursor-pointer transition-colors">
                  <td className="pl-2 text-[var(--color-text-tertiary)]">{abierta === r.clave ? <ChevronDown size={14} /> : <ChevronRight size={14} />}</td>
                  <td className="px-3 py-2.5 text-xs max-w-[360px]">
                    <div className="text-[var(--color-text)]">{r.nombre || r.titulo_ejemplo || '—'}</div>
                    {r.estado === 'desconocida' && <div className="text-[10px] text-amber-500">{t('pub.families.unknownHint')}</div>}
                  </td>
                  <td className="px-3 py-2.5 text-center text-xs font-mono">{r.miembros}</td>
                  <td className="px-3 py-2.5 text-xs">
                    <span className="font-mono text-[var(--color-text-secondary)]">{r.categoria || '—'}</span>
                    {r.categorias > 1 && (
                      <span className={`${chip} ${bad} ml-1`}>{t('pub.families.multiCategory', { n: r.categorias })}</span>
                    )}
                  </td>
                  <td className="px-3 py-2.5 text-right text-xs font-mono">{r.ventas_90d}</td>
                  <td className="px-3 py-2.5 text-xs text-[var(--color-text-secondary)] whitespace-nowrap">
                    {r.actualizado ? new Date(r.actualizado).toLocaleDateString() : '—'}
                  </td>
                </tr>
                {abierta === r.clave && (
                  <tr className="border-b border-[var(--color-border)]">
                    <td colSpan={6}><DetalleFamilia clave={r.clave} sellerId={sellerId} /></td>
                  </tr>
                )}
              </Fragment>
            ))}
          </tbody>
        </table>
      </div>

      <Pagination page={pag.page} totalPages={pag.totalPages} total={pag.total} perPage={pag.perPage}
        onPageChange={pag.setPage} onPerPageChange={pag.setPerPage} />
    </div>
  );
}

export { FamiliesView };
